import React, { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { useTypedSelector } from '@hooks';
import { AssetReviewAssetItemDto, AWAssetListItemDto } from '@api/Api';
import { ReactComponent as ArrowRightSvg } from '@assets/icons/arrow-right-without-line.svg';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as ArrowCheckSvg } from '@assets/icons/check-task.svg';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { sortedAWListAssetsToReviewInBatch } from '@components/Modals/ApprovalModal/utils';
import { setAsset, setAssetVariant } from '@redux/actions/mediaViewerAction';
import './AssetsSlider.less';
import { useAssetReview } from '@components/AssetReviewProvider';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { nonNull } from '@helpers/non-null';
import { useAWListAssetsToReviewInBatch } from '@hooks/queries/approval';

type AssetsSliderItemProps = {
  asset: AWAssetListItemDto | AssetReviewAssetItemDto;
  isActive: boolean;
  onClick: () => void;
};

function isApproval(
  asset: AWAssetListItemDto | AssetReviewAssetItemDto
): asset is AWAssetListItemDto {
  return 'currentStageId' in asset;
}

function isOpenDisabled(asset: AWAssetListItemDto | AssetReviewAssetItemDto) {
  if (isApproval(asset)) return asset.isPaused || asset.isFutureReview;
  return false;
}

function AssetsSliderItem({ asset, isActive, onClick }: AssetsSliderItemProps) {
  const isPaused = isApproval(asset) ? asset.isPaused : false;
  const isFinished = isApproval(asset) ? asset.isFinished : false;
  const isFutureReview = isApproval(asset) ? asset.isFutureReview : false;
  const lockDecisions = isApproval(asset) ? asset.lockDecisions : false;
  const decision = isApproval(asset) ? asset.decision : null;
  const disableReason = isPaused ? (
    <>
      Approval is currently paused.
      <br />
      You will be notified when it is resumed.
    </>
  ) : isFinished ? (
    <>
      Approval is already finished.
      <br />
      Contact the approval manager <br />
      if you need to change your decision.
    </>
  ) : lockDecisions && decision ? (
    <>
      Your decision is locked.
      <br />
      Contact the approval manager
      <br />
      if you need to change it.
    </>
  ) : isFutureReview ? (
    <>
      Approval is scheduled for a future review.
      <br /> Please check back later.
    </>
  ) : null;

  const showLock =
    isPaused || isFinished || isFutureReview || (!!decision && lockDecisions);
  const grayOut = isPaused || isFinished || isFutureReview || !!decision;
  const disabledOpen = isPaused || isFutureReview;

  const previewUrl = isApproval(asset)
    ? asset.previewUrl
    : asset.asset?.versions[0]?.metadata.preview.url;

  const assetType = isApproval(asset)
    ? asset.type
    : asset.asset?.versions[0]?.metadata.type;

  const preparePreview = useMemo(() => {
    const res = assetPreviewThumbnails({
      url: previewUrl,
      status: 'ready',
      type: assetType || 'unknown',
      format: 'img'
    });

    if (typeof res === 'string')
      return <img draggable={false} src={res || ''} alt="" />;
    return res;
  }, [previewUrl, assetType]);

  const name = isApproval(asset) ? asset.name : asset.asset?.versions[0]?.name;

  const extension = isApproval(asset)
    ? asset.extension
    : asset.asset?.versions[0]?.extension;

  const versionNumber = isApproval(asset)
    ? asset.versionsCount
    : asset.asset?.versions[0]?.versionNumber;

  return (
    <Tooltip
      overlayClassName="assets-slider_tooltip"
      title={
        <>
          {!disableReason && (
            <div className="assets-slider_tooltip-head">
              <div className="assets-slider_tooltip-name">
                {name}
                <span className="assets-slider_tooltip-extension">
                  .{extension}
                </span>
              </div>
              <div className="assets-slider_tooltip-version">
                V{versionNumber}
              </div>
            </div>
          )}
          {disableReason && (
            <div className="assets-slider_tooltip-body">{disableReason}</div>
          )}
        </>
      }
    >
      <div
        className={classNames('assets-slider_item', {
          'assets-slider_item--active': isActive,
          'assets-slider_item--disabled-open': disabledOpen,
          'assets-slider_item--disabled': grayOut
        })}
        onClick={onClick}
      >
        {!!showLock && (
          <div className="assets-slider_item-lock-icon">
            <LockSvg />
          </div>
        )}
        {preparePreview}
        {!!decision && (
          <div
            className={classNames([
              'assets-slider_item-icon',
              `b-color-${decision}`
            ])}
          >
            <ArrowCheckSvg />
          </div>
        )}
      </div>
    </Tooltip>
  );
}

export default function AssetsSlider() {
  const location = useLocation();
  const navigate = useNavigate();
  const review = useAssetReview() as
    | ReturnType<typeof useAssetReview>
    | undefined;
  const dispatch = useTypedDispatch();

  const isInternalMediaViewer = location.pathname.includes('media-viewer');
  const selectedAsset = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.assets
  );
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedAssetId = selectedAsset?.id;
  const contentRef = useRef<HTMLDivElement>(null);

  const { data: approvalReviewInBatch } = useAWListAssetsToReviewInBatch({
    initialData: review?.listAssetsInBatch,
    enabled: !review?.content,
    params: {
      batchId: review?.batchId ?? selectedVersion?.approvalWorkflow?.batchId,
      password: review?.password
    }
  });

  const sortedAssets = useMemo(() => {
    if (review?.content?.assets) return review?.content?.assets;
    if (!approvalReviewInBatch?.assets) return [];
    const _approvalReviewInBatch = approvalReviewInBatch?.assets.filter(
      (item) => !item.isPaused || !item.isFinished
    );
    return sortedAWListAssetsToReviewInBatch(_approvalReviewInBatch);
  }, [review?.content?.assets, approvalReviewInBatch?.assets]);

  const count = sortedAssets.length;

  const onChangeAsset = (
    nextAsset: AWAssetListItemDto | AssetReviewAssetItemDto
  ) => {
    if (!nextAsset) return;
    if (isApproval(nextAsset)) {
      if (isInternalMediaViewer && nextAsset.hasAccess) {
        navigate(`/media-viewer/${nextAsset.id}/${nextAsset.versionId}`);
        return;
      }
      if (isInternalMediaViewer && !nextAsset.hasAccess) {
        navigate(`/approval?reviewId=${nextAsset.versionId}`);
        return;
      }
      searchParams.set('reviewId', nextAsset.versionId);
      setSearchParams(searchParams);
    } else {
      dispatch(setAsset(nextAsset));
      dispatch(setAssetVariant(nonNull(nextAsset.asset).versions[0].id));
    }
  };

  const onPrev = () => {
    const assets = [...sortedAssets].reverse();
    const currentIndex = assets.findIndex(
      (item) => item.id === selectedAssetId
    );
    let prevAsset = assets.find((asset, i) => {
      return !isOpenDisabled(asset) && i > currentIndex;
    });
    if (!prevAsset) {
      prevAsset = assets.find((asset, i) => {
        return !isOpenDisabled(asset) && i < currentIndex;
      });
    }
    if (prevAsset) onChangeAsset(prevAsset);
  };

  const onNext = () => {
    const currentIndex = sortedAssets.findIndex(
      (item) => item.id === selectedAssetId
    );
    let nextAsset = sortedAssets.find((asset, i) => {
      return !isOpenDisabled(asset) && i > currentIndex;
    });
    if (!nextAsset) {
      nextAsset = sortedAssets.find((asset, i) => {
        return !isOpenDisabled(asset) && i < currentIndex;
      });
    }
    if (nextAsset) onChangeAsset(nextAsset);
  };

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    if (!sortedAssets.length) return;
    const currentIndex = sortedAssets.findIndex(
      (item) => item.id === selectedAssetId
    );
    const itemElement = content.children[currentIndex];
    if (itemElement) {
      itemElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start'
      });
    }
  }, [sortedAssets, selectedAssetId]);

  if (count <= 1) return null;

  return (
    <div className="assets-slider">
      <button
        className="assets-slider_btn assets-slider_btn-prev"
        type="button"
        onClick={onPrev}
      >
        <ArrowLeftSvg />
      </button>
      <div ref={contentRef} className="assets-slider_content">
        {sortedAssets.map((item) => {
          const isActive = selectedAssetId === item.id;
          return (
            <AssetsSliderItem
              asset={item}
              isActive={isActive}
              onClick={() => {
                if (isActive) return;
                if (isOpenDisabled(item)) return;
                onChangeAsset(item);
              }}
              key={item.id}
            />
          );
        })}
      </div>
      <button
        className="assets-slider_btn assets-slider_btn-next"
        type="button"
        onClick={onNext}
      >
        <ArrowRightSvg />
      </button>
    </div>
  );
}
